import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
  animation = 'pulse',
}) => {
  const baseClasses = 'bg-gray-200';
  const animationClasses = animation === 'pulse' ? 'animate-pulse' : 'animate-shimmer';
  
  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const style: React.CSSProperties = {
    width: width || '100%',
    height: height || (variant === 'text' ? '1em' : '100%'),
  };

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${animationClasses} ${className}`}
      style={style}
    />
  );
};

// Skeleton Card Component
export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
    <div className="animate-pulse space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton width="40%" height="1.5rem" />
        <Skeleton variant="circular" width="2rem" height="2rem" />
      </div>
      <Skeleton width="60%" height="2.5rem" />
      <Skeleton width="30%" height="1rem" />
    </div>
  </div>
);

// Skeleton Table Component
export const SkeletonTable: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 6,
}) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <div className="animate-pulse">
      {/* Header */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <div className="flex space-x-4">
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} width={`${100 / columns}%`} height="1rem" />
          ))}
        </div>
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="px-6 py-4 border-b border-gray-100">
          <div className="flex space-x-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={colIndex} width={`${100 / columns}%`} height="1rem" />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Skeleton Chart Component
export const SkeletonChart: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
    <div className="animate-pulse space-y-4">
      <Skeleton width="40%" height="1.5rem" />
      <div className="h-64 flex items-end justify-between space-x-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton
            key={i}
            width="100%"
            height={`${Math.random() * 60 + 40}%`}
            variant="rectangular"
          />
        ))}
      </div>
      <div className="flex justify-between">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} width="10%" height="0.75rem" />
        ))}
      </div>
    </div>
  </div>
);

// Skeleton List Component
export const SkeletonList: React.FC<{ items?: number; className?: string }> = ({
  items = 5,
  className = '',
}) => (
  <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
    <div className="animate-pulse space-y-4">
      <Skeleton width="40%" height="1.5rem" />
      <div className="space-y-3">
        {Array.from({ length: items }).map((_, i) => (
          <div key={i} className="flex items-center space-x-3">
            <Skeleton variant="circular" width="0.5rem" height="0.5rem" />
            <Skeleton variant="circular" width="1.25rem" height="1.25rem" />
            <Skeleton width="70%" height="1rem" />
            <Skeleton width="15%" height="0.75rem" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Skeleton Metric Cards Grid
export const SkeletonMetrics: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

// Skeleton Dashboard
export const SkeletonDashboard: React.FC = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="animate-pulse space-y-2">
      <Skeleton width="30%" height="2rem" />
      <Skeleton width="50%" height="1rem" />
    </div>

    {/* Metrics */}
    <SkeletonMetrics />

    {/* Charts */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <SkeletonChart />
      <SkeletonChart />
    </div>

    {/* Table */}
    <SkeletonTable />

    {/* Activity and Status */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <SkeletonList />
      <SkeletonList items={4} />
    </div>
  </div>
);

export default Skeleton;
